import { type Subscriptions } from "../index";

/**
 * One realtime subscription of the app - the pair of `<name>ChannelName` and
 * `realtime<Name>Channel` fields on `subscriptions`. Chat conversations get one slot per
 * channel id.
 */
export type RealtimeSlot =
  | "userProfile"
  | "notifications"
  | "team"
  | "teamMember"
  | "products"
  | "product"
  | "productAutomation"
  | "productClient"
  | "productsSettings"
  | "discounts"
  | "mediaLibrary"
  | "trainee"
  | "chatLatest"
  | "chatUnread"
  | `chatChannel:${string}`;

/**
 * Take ownership of a slot. Call this as the FIRST statement of a subscribe/unsubscribe
 * thunk, before anything is awaited.
 *
 * Subscribe thunks fetch data (and often resolve the logged user) before they touch the
 * channel fields. Two overlapping runs - a tab regaining focus, a quick navigation between
 * clients - therefore read the same handle, both remove it and both create a channel;
 * whichever assigned first is left joined on the socket with nothing pointing at it.
 * Claiming the slot up front means the older run can notice it lost the slot and stop.
 */
export const claimRealtimeSlot = (subscriptions: Subscriptions, slot: RealtimeSlot): number => {
  if (!subscriptions.channelGenerations) {
    subscriptions.channelGenerations = {};
  }

  const claimed = (subscriptions.channelGenerations[slot] ?? 0) + 1;
  subscriptions.channelGenerations[slot] = claimed;

  return claimed;
};

/**
 * Whether this run still owns the slot it claimed. Check it after every await and before
 * creating the channel - a `false` means a newer subscribe or an unsubscribe took over,
 * so this run must not touch the channel fields any more.
 */
export const ownsRealtimeSlot = (subscriptions: Subscriptions, slot: RealtimeSlot, claimed: number): boolean =>
  subscriptions.channelGenerations?.[slot] === claimed;
